import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ById, CurrencyObject, DateFormatObject, DeliveryOption, Languages } from 'common/types';
import React from 'react';
import { TFunction } from 'react-i18next';
import { BookingsTableRow } from './rows';
import { getPricingString } from 'common/utils/common';
import { getTranslation } from 'common/modules/translations';
import { localFormat } from 'common/utils/dateUtils';
import moment from 'moment-timezone';
import BookingStateBadge from '../components/BookingStateBadge';
import BookingPaymentStateBadge from '../components/BookingPaymentStateBadge';
import BookingPreparationStateBadge from '../components/BookingPreparationStateBadge';
import {
	getBookingPaymentStateValueOptions,
	getBookingPreparationStateValueOptions,
	getBookingStateValueOptions,
} from './labels';
import { Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/system';

interface BookingsTableColDef extends GridColDef {
	field: keyof BookingsTableRow;
	renderCell?: (params: GridRenderCellParams<any, BookingsTableRow>) => React.ReactNode;
}

export const getBookingsTableColumns = (deps: {
	t: TFunction;
	lang: Languages;
	currency: CurrencyObject;
	dateFormat: DateFormatObject;
	deliveryOptions: ById<DeliveryOption>;
	hiddenColumns?: Array<keyof BookingsTableRow>;
	primaryDateColumn: 'created' | 'completed';
}): GridColDef[] => {
	const { t, lang, currency, dateFormat, hiddenColumns, primaryDateColumn, deliveryOptions } = deps;

	const now = moment();
	const getFormattedDate = (date: string | undefined) => {
		if (!date) return '-';
		const momentDate = moment(date);

		if (now.diff(momentDate, 'weeks') <= 2) return localFormat(momentDate, 'ddd D.M', dateFormat);
		if (momentDate.isSame(now, 'year')) return localFormat(momentDate, 'DD.MM.', dateFormat);

		return localFormat(momentDate, 'DD.MM.YYYY', dateFormat);
	};

	const getFormattedDateAndTime = (date: string) => {
		if (!date) return '-';
		const momentDate = moment(date);

		return localFormat(momentDate, 'DD.MM.YYYY, HH:mm', dateFormat);
	};

	const getFormattedDeliveryOption = (id: string | null) => {
		if (!id) return '-';
		const option = deliveryOptions[id];
		if (!option) return id;
		return getTranslation(option.name, lang);
	};

	const columns: BookingsTableColDef[] = [
		{
			field: primaryDateColumn,
			headerName: t('BookingsTable.Date', 'Date'),
			valueFormatter: ({ value }) => getFormattedDate(value as string),
			minWidth: 150,
			flex: 1,
			type: 'date',
			filterable: false,
		},
		{
			field: 'customer',
			headerName: t('BookingsTable.Customer', 'Customer'),
			minWidth: 200,
			filterable: false,
		},
		{
			field: 'includedProductNames',
			headerName: t('BookingsTable.Products', 'Products'),
			minWidth: 250,
			cellClassName: 'overflow-cell',
			renderCell: ({ value = [] }: { value?: string[] }) => {
				const MAX_ROWS = 2;
				const productsToShow = value.slice(0, MAX_ROWS);
				const productsToNotShow = value.slice(MAX_ROWS);

				return (
					<Stack direction="column">
						{productsToShow.map((name, index) => (
							<Stack direction="row" spacing={1} key={name}>
								<Typography fontSize="1.4rem">{name}</Typography>
								{productsToNotShow.length > 0 && index === productsToShow.length - 1 && (
									<Tooltip
										title={
											<Stack direction="column">
												{productsToNotShow.map((name) => (
													<Typography fontSize="1.4rem" key={name}>
														{name}
													</Typography>
												))}
											</Stack>
										}
									>
										<PlusChip>+{productsToNotShow.length}</PlusChip>
									</Tooltip>
								)}
							</Stack>
						))}
					</Stack>
				);
			},
		},
		{
			field: 'state',
			headerName: t('BookingsTable.Status', 'Status'),
			minWidth: 150,
			renderCell: ({ value }) => <BookingStateBadge state={value} />,
			type: 'singleSelect',
			valueOptions: getBookingStateValueOptions(t),
		},
		{
			field: 'paymentState',
			headerName: t('BookingsTable.Payment', 'Payment'),
			minWidth: 150,
			renderCell: ({ value }) => <BookingPaymentStateBadge paymentState={value} />,
			type: 'singleSelect',
			valueOptions: getBookingPaymentStateValueOptions(t),
		},
		{
			field: 'preparationState',
			headerName: t('BookingsTable.Preparation', 'Preparation'),
			minWidth: 150,
			renderCell: ({ value }) => <BookingPreparationStateBadge preparationState={value} />,
			type: 'singleSelect',
			valueOptions: getBookingPreparationStateValueOptions(t),
		},
		{
			field: 'price',
			headerName: t('BookingsTable.Price', {
				currency: currency.displayAs,
				defaultValue: 'Price ({{currency}})',
			}),
			valueFormatter: ({ value }) => getPricingString(value as number),
			align: 'right',
			type: 'number',
			minWidth: 150,
		},
		{
			field: 'startDate',
			headerName: t('BookingsTable.StartDate', 'Start'),
			valueFormatter: ({ value }) => getFormattedDateAndTime(value as string),
			minWidth: 200,
			type: 'date',
			filterable: false,
		},
		{
			field: 'endDate',
			headerName: t('BookingsTable.EndDate', 'End'),
			valueFormatter: ({ value }) => getFormattedDateAndTime(value as string),
			minWidth: 200,
			type: 'date',
			filterable: false,
		},
		{
			field: 'deliveryOptionId',
			headerName: t('BookingsTable.Delivery', 'Delivery'),
			minWidth: 200,
			valueFormatter: ({ value }) => getFormattedDeliveryOption(value as string | null),
			type: 'singleSelect',
			valueOptions: Object.values(deliveryOptions).map((option) => ({
				value: option.id,
				label: getFormattedDeliveryOption(option.id),
			})),
		},
	];

	return columns.filter((col) => !hiddenColumns?.includes(col.field));
};

const PlusChip = styled(Typography)(({ theme }) => ({
	border: `1px solid ${theme.palette.border.inputOutline}`,
	borderRadius: 16,
	padding: theme.spacing(0.25, 1.5),
	fontSize: '1.2rem',
}));
